import { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import confetti from "canvas-confetti";
import {
  Coins, Users, Gift, Lock, Unlock, ArrowLeft, Sparkles, TrendingUp,
  History, Copy, CheckCircle2, Award, X,
} from "lucide-react";
import { useMember } from "@/contexts/MemberContext";
import { memberApi, formatMemberError } from "@/lib/memberClient";
import { BRAND } from "@/constants/brand";

const QUICK = [25, 50, 100, 200];

export default function MemberVault() {
  const { member, loading } = useMember();
  const nav = useNavigate();
  const [vault, setVault] = useState(null);
  const [wallet, setWallet] = useState({ balance: 0 });
  const [coins, setCoins] = useState(50);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [opening, setOpening] = useState(false);
  const [ceremony, setCeremony] = useState(null); // reward payload after opening
  const [tab, setTab] = useState("contribute"); // contribute | history

  useEffect(() => { if (!loading && !member) nav("/member/login", { replace: true }); }, [member, loading, nav]);

  const load = async () => {
    try {
      const [{ data: v }, { data: w }] = await Promise.all([
        memberApi.get("/member/vault"),
        memberApi.get("/member/wallet"),
      ]);
      setVault(v);
      setWallet(w);
    } catch (e) { toast.error(formatMemberError(e)); }
  };
  useEffect(() => { if (member) load(); /* eslint-disable-next-line */ }, [member?.id]);

  const percent = vault?.percent || 0;
  const campaign = vault?.campaign;
  const isUnlocked = campaign?.status === "unlocked";
  const alreadyOpened = !!vault?.opened;
  const canOpen = isUnlocked && (vault?.personal_total || 0) > 0;

  const dailyLeft = useMemo(() => {
    if (!campaign) return 0;
    return Math.max(0, (campaign.daily_coin_limit || 0) - (vault?.coins_used_today || 0));
  }, [campaign, vault]);

  const submitContribute = async () => {
    if (!campaign) return;
    const n = parseInt(coins) || 0;
    if (n < (campaign.coin_min || 1)) return toast.error(`Minimum ${campaign.coin_min} coins`);
    if (n > (campaign.coin_max || 999999)) return toast.error(`Maximum ${campaign.coin_max} coins`);
    if (n > (wallet.balance || 0)) return toast.error(`You only have ${wallet.balance} Pop Coins`);
    setBusy(true);
    try {
      const { data } = await memberApi.post("/member/vault/contribute", { coins: n, note });
      toast.success(`+${data.value} vault points added!`);
      confetti({ particleCount: 90, spread: 80, origin: { y: 0.4 }, colors: ["#FFC93C", "#FFE07A", "#E5A100", "#fff"] });
      setNote("");
      await load();
    } catch (e) { toast.error(formatMemberError(e)); }
    finally { setBusy(false); }
  };

  const openVault = async () => {
    if (!canOpen || alreadyOpened) return;
    setOpening(true);
    // Cinematic sequence: 4s of theatrics before hitting the API
    try {
      await new Promise((r) => setTimeout(r, 2600));
      const { data } = await memberApi.post("/member/vault/open");
      setCeremony(data.opening);
      // Massive confetti storm
      const shootConfetti = () => confetti({ particleCount: 160, spread: 100, origin: { y: 0.35 }, colors: ["#FFC93C", "#FFE07A", "#E5A100", "#FF4F5A", "#fff"] });
      shootConfetti();
      setTimeout(shootConfetti, 300);
      setTimeout(shootConfetti, 700);
      setTimeout(() => confetti({ particleCount: 100, spread: 140, origin: { y: 0.6 } }), 1000);
      await load();
    } catch (e) {
      toast.error(formatMemberError(e));
    } finally {
      setOpening(false);
    }
  };

  if (loading || !member) return <div className="min-h-screen grid place-items-center bg-[#0a0714] text-brand-cream">Loading…</div>;
  if (!vault) return <div className="min-h-screen grid place-items-center bg-[#0a0714] text-brand-cream">Loading vault…</div>;
  if (vault.active === false) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#0a0714] text-brand-cream text-center px-6">
        <div>
          <div className="text-brand-yellow uppercase tracking-[0.28em] text-xs mb-2">Community Vault</div>
          <div className="font-display text-3xl">No active vault campaign right now.</div>
          <Link to="/member/dashboard" className="mt-6 inline-flex items-center gap-2 text-sm text-brand-cream/70 hover:text-brand-cream"><ArrowLeft size={14} /> Back to dashboard</Link>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="member-vault-page"
      className="min-h-screen relative overflow-hidden"
      style={{
        background:
          "radial-gradient(ellipse at 20% 20%, #1e0d3e 0%, #0e0a24 40%, #0a0714 80%, #050308 100%)",
      }}
    >
      {/* Ambient background */}
      <BgOrbs />

      {/* Top bar */}
      <header className="relative z-10 flex items-center justify-between px-6 lg:px-12 py-6 text-brand-cream">
        <Link to="/member/dashboard" className="flex items-center gap-3" data-testid="vault-back">
          <ArrowLeft size={16} className="text-brand-cream/70" />
          <img src={BRAND.logoUrl} alt="Pop Panda" className="h-9 w-9 bg-brand-cream rounded-full p-1.5" />
          <div className="font-display text-xl">Community Vault</div>
        </Link>
        <div className="flex items-center gap-3">
          <div className="hidden sm:flex items-center gap-2 rounded-full bg-brand-yellow/15 border border-brand-yellow/30 backdrop-blur-lg px-4 py-1.5 text-sm text-brand-yellow" data-testid="vault-wallet-chip">
            <Coins size={14} /> {wallet.balance} Pop Coins
          </div>
        </div>
      </header>

      <main className="relative z-10 max-w-6xl mx-auto px-6 lg:px-12 pb-24">
        {/* Hero */}
        <div className="grid lg:grid-cols-[1.1fr_1fr] gap-10 items-center">
          <div className="relative min-h-[500px] flex items-center justify-center">
            <VaultVisual percent={percent} unlocked={isUnlocked} opening={opening} />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.32em] text-brand-yellow inline-flex items-center gap-2">
              <Sparkles size={12} /> {isUnlocked ? "UNLOCKED — Claim your reward" : "Contribute to unlock"}
            </div>
            <h1 className="font-display text-5xl mt-3 text-brand-cream leading-tight" data-testid="vault-title">
              {campaign.title}
            </h1>
            <p className="mt-4 text-brand-cream/70 max-w-lg" data-testid="vault-description">{campaign.description}</p>

            {/* Progress meter */}
            <div className="mt-6 rounded-2xl bg-white/[0.06] border border-white/15 backdrop-blur-xl p-5">
              <div className="flex items-center justify-between text-sm text-brand-cream/70">
                <span>Community progress</span>
                <span className="text-brand-yellow font-mono" data-testid="vault-percent">
                  {campaign.current_progress} / {campaign.community_goal} ({Math.round(percent)}%)
                </span>
              </div>
              <div className="mt-2 h-3 rounded-full bg-white/10 overflow-hidden">
                <motion.div
                  className="h-full rounded-full"
                  style={{
                    background: "linear-gradient(90deg, #FFE07A 0%, #FFC93C 50%, #E5A100 100%)",
                    boxShadow: "0 0 12px rgba(255,201,60,0.6)",
                  }}
                  initial={{ width: 0 }}
                  animate={{ width: `${Math.min(100, percent)}%` }}
                  transition={{ duration: 1.6, ease: "easeOut" }}
                />
              </div>
              <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                <MiniStat label="Contributors" value={campaign.contributing_members_count || 0} icon={Users} />
                <MiniStat label="Your points" value={vault.personal_total || 0} icon={Award} />
                <MiniStat label="Your lifetime" value={vault.lifetime_contribution || 0} icon={TrendingUp} />
              </div>
            </div>

            {/* Reward preview */}
            <div className="mt-5 rounded-2xl border border-brand-yellow/30 bg-brand-yellow/[0.08] backdrop-blur-xl p-5">
              <div className="text-[10px] uppercase tracking-[0.28em] text-brand-yellow flex items-center gap-2">
                <Gift size={12} /> Reward inside
              </div>
              <div className="mt-1.5 font-display text-2xl text-brand-cream">{campaign.reward_title}</div>
              <div className="text-sm text-brand-cream/70 mt-1">{campaign.reward_description}</div>
            </div>

            {/* Open button */}
            {isUnlocked && (
              <div className="mt-6">
                {alreadyOpened ? (
                  <div className="rounded-2xl border border-emerald-400/40 bg-emerald-500/10 backdrop-blur-xl p-4 flex items-center gap-3 text-brand-cream" data-testid="vault-already-opened">
                    <CheckCircle2 size={20} className="text-emerald-400" />
                    <div>
                      <div className="font-medium">You&apos;ve already opened this Vault!</div>
                      <div className="text-xs text-brand-cream/60">Code: <span className="font-mono text-brand-yellow">{vault.opening?.reward_code}</span></div>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={openVault}
                    disabled={!canOpen || opening}
                    data-testid="vault-open-button"
                    className="relative group rounded-full px-8 py-4 text-base font-semibold inline-flex items-center gap-2 overflow-hidden text-[#1a0f2f] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300"
                    style={{
                      background: "linear-gradient(135deg, #FFE07A 0%, #FFC93C 50%, #E5A100 100%)",
                      boxShadow: "0 12px 32px rgba(255,201,60,0.5), inset 0 1px 0 rgba(255,255,255,0.4)",
                    }}
                  >
                    <Unlock size={18} /> {opening ? "Opening…" : "Open the Vault"}
                  </button>
                )}
                {!canOpen && !alreadyOpened && (
                  <div className="mt-2 text-xs text-brand-cream/60">You need to contribute at least once to open the Vault.</div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Tabs — Contribute / History */}
        {!isUnlocked && (
          <div className="mt-14">
            <div className="flex gap-2 border-b border-white/10">
              {[
                { k: "contribute", l: "Contribute", icon: Coins },
                { k: "history", l: "Your history", icon: History },
              ].map((t) => (
                <button
                  key={t.k}
                  onClick={() => setTab(t.k)}
                  data-testid={`vault-tab-${t.k}`}
                  className={`px-5 py-3 text-sm uppercase tracking-[0.2em] transition-colors border-b-2 inline-flex items-center gap-2 ${
                    tab === t.k
                      ? "border-brand-yellow text-brand-yellow"
                      : "border-transparent text-brand-cream/60 hover:text-brand-cream"
                  }`}
                >
                  <t.icon size={12} /> {t.l}
                </button>
              ))}
            </div>

            <div className="mt-6">
              {tab === "contribute" && (
                <div className="grid lg:grid-cols-[1fr_1fr] gap-6">
                  {/* Contribute form */}
                  <div className="rounded-2xl bg-white/[0.06] border border-white/15 backdrop-blur-xl p-6">
                    <div className="text-[11px] uppercase tracking-widest text-brand-yellow">Add Pop Coins to the Vault</div>
                    <div className="mt-1 font-display text-2xl text-brand-cream">Every coin fills the community.</div>

                    <div className="mt-5">
                      <label className="text-[10px] uppercase tracking-widest text-brand-cream/60">Coins to contribute</label>
                      <div className="mt-1 flex items-center gap-2 rounded-xl bg-black/30 border border-white/15 px-4 py-3">
                        <Coins size={16} className="text-brand-yellow" />
                        <input
                          type="number"
                          min={campaign.coin_min}
                          max={Math.min(campaign.coin_max, wallet.balance)}
                          value={coins}
                          onChange={(e) => setCoins(e.target.value)}
                          data-testid="vault-input-coins"
                          className="flex-1 bg-transparent outline-none text-brand-cream font-mono text-lg"
                        />
                      </div>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {QUICK.map((q) => (
                          <button
                            key={q}
                            onClick={() => setCoins(q)}
                            data-testid={`vault-quick-${q}`}
                            className="text-xs rounded-full bg-white/8 hover:bg-white/16 border border-white/20 text-brand-cream/80 px-3 py-1 transition"
                          >
                            +{q}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="mt-4">
                      <label className="text-[10px] uppercase tracking-widest text-brand-cream/60">Note (optional)</label>
                      <input
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        data-testid="vault-input-note"
                        placeholder="A shoutout, cheer, or dedication…"
                        className="mt-1 w-full rounded-xl bg-black/30 border border-white/15 px-4 py-3 text-sm text-brand-cream outline-none focus:border-brand-yellow"
                      />
                    </div>

                    <button
                      onClick={submitContribute}
                      disabled={busy}
                      data-testid="vault-submit-contribute"
                      className="mt-5 w-full rounded-full px-6 py-3.5 text-sm font-semibold text-[#1a0f2f] disabled:opacity-60 transition-all"
                      style={{
                        background: "linear-gradient(135deg, #FFE07A 0%, #FFC93C 50%, #E5A100 100%)",
                        boxShadow: "0 10px 28px rgba(255,201,60,0.4)",
                      }}
                    >
                      {busy ? "Contributing…" : `Contribute ${coins || 0} Pop Coins`}
                    </button>

                    <div className="mt-4 grid grid-cols-2 gap-2 text-xs text-brand-cream/60">
                      <div>Min: <span className="text-brand-cream">{campaign.coin_min}</span></div>
                      <div>Max single: <span className="text-brand-cream">{campaign.coin_max}</span></div>
                      <div>Daily left: <span className="text-brand-cream">{dailyLeft}</span></div>
                      <div>Wallet: <span className="text-brand-cream">{wallet.balance}</span></div>
                    </div>
                  </div>

                  {/* Impact card */}
                  <div className="rounded-2xl bg-white/[0.06] border border-white/15 backdrop-blur-xl p-6">
                    <div className="text-[11px] uppercase tracking-widest text-brand-yellow">Your impact</div>
                    <div className="mt-1 font-display text-2xl text-brand-cream">You&apos;re a builder of this vault.</div>
                    <div className="mt-5 space-y-4">
                      <Row label="Your points this campaign" value={vault.personal_total || 0} />
                      <Row label="Your Pop Coins used" value={vault.coins_used_campaign || 0} />
                      <Row label="Contribution today" value={vault.coins_used_today || 0} />
                      <Row label="Lifetime vault points" value={vault.lifetime_contribution || 0} />
                    </div>
                    <div className="mt-6 rounded-xl bg-black/30 border border-white/10 p-4">
                      <div className="text-[10px] uppercase tracking-widest text-brand-cream/60">Conversion</div>
                      <div className="mt-1 text-sm text-brand-cream/85">
                        <span className="text-brand-yellow font-mono">{Math.round(1 / (campaign.coin_to_progress || 0.1))}</span> Pop Coins = <span className="text-brand-yellow font-mono">1</span> Vault point.
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {tab === "history" && (
                <div className="rounded-2xl bg-white/[0.06] border border-white/15 backdrop-blur-xl p-6">
                  <div className="text-[11px] uppercase tracking-widest text-brand-yellow">Your contribution history</div>
                  {(vault.personal_history || []).length === 0 ? (
                    <div className="mt-6 text-center text-brand-cream/60 py-8">You haven&apos;t contributed yet. Add your first coins above!</div>
                  ) : (
                    <ul className="mt-4 divide-y divide-white/10">
                      {vault.personal_history.map((h) => (
                        <li key={h.id} className="py-3 flex items-center justify-between text-sm" data-testid={`vault-hist-${h.id}`}>
                          <div>
                            <div className="text-brand-cream capitalize">{h.source} contribution</div>
                            {h.note && <div className="text-xs text-brand-cream/50">&ldquo;{h.note}&rdquo;</div>}
                            <div className="text-[10px] text-brand-cream/40 mt-0.5">{new Date(h.created_at).toLocaleString()}</div>
                          </div>
                          <div className="text-right">
                            <div className="font-mono text-brand-yellow">+{h.value} pts</div>
                            {h.coins_used > 0 && <div className="text-[10px] text-brand-cream/50">-{h.coins_used} coins</div>}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </main>

      {/* Opening ceremony */}
      <AnimatePresence>
        {opening && !ceremony && <CeremonyOverlay />}
        {ceremony && <RewardModal ceremony={ceremony} campaign={campaign} onClose={() => setCeremony(null)} />}
      </AnimatePresence>
    </div>
  );
}

/* ---------------- Sub Components ---------------- */

function BgOrbs() {
  return (
    <div className="absolute inset-0 pointer-events-none">
      <motion.div
        className="absolute -top-40 -left-32 w-[560px] h-[560px] rounded-full blur-3xl"
        style={{ background: "radial-gradient(circle, rgba(255,201,60,0.22), transparent 60%)" }}
        animate={{ x: [0, 30, 0], y: [0, 20, 0] }}
        transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute bottom-0 -right-24 w-[440px] h-[440px] rounded-full blur-3xl"
        style={{ background: "radial-gradient(circle, rgba(139,92,246,0.24), transparent 60%)" }}
        animate={{ x: [0, -30, 0], y: [0, -20, 0] }}
        transition={{ duration: 16, repeat: Infinity, ease: "easeInOut" }}
      />
      {Array.from({ length: 40 }).map((_, i) => (
        <span
          key={i}
          className="absolute rounded-full bg-white"
          style={{
            left: `${(i * 41) % 100}%`,
            top: `${(i * 67) % 100}%`,
            width: 2, height: 2, opacity: 0.15 + (i % 4) * 0.08,
          }}
        />
      ))}
    </div>
  );
}

function VaultVisual({ percent, unlocked, opening }) {
  const size = 460;
  const cx = size / 2, cy = size / 2, ringR = 200;
  const circumference = 2 * Math.PI * ringR;
  const dashOffset = circumference * (1 - Math.min(1, percent / 100));

  return (
    <div className="relative" style={{ width: size, height: size }}>
      {/* Rays */}
      <motion.div
        className="absolute inset-0 pointer-events-none"
        animate={{ rotate: opening ? 720 : 360 }}
        transition={{ duration: opening ? 2.6 : 60, repeat: opening ? 0 : Infinity, ease: opening ? "easeInOut" : "linear" }}
      >
        <div
          className="absolute inset-0 rounded-full"
          style={{
            background:
              "conic-gradient(from 0deg, rgba(255,201,60,0.16) 0deg, transparent 20deg, rgba(255,201,60,0.16) 40deg, transparent 60deg, rgba(255,201,60,0.16) 80deg, transparent 100deg, rgba(255,201,60,0.16) 120deg, transparent 140deg, rgba(255,201,60,0.16) 160deg, transparent 180deg)",
            maskImage: "radial-gradient(circle, transparent 55%, black 70%, transparent 95%)",
            WebkitMaskImage: "radial-gradient(circle, transparent 55%, black 70%, transparent 95%)",
          }}
        />
      </motion.div>

      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="absolute inset-0">
        <defs>
          <linearGradient id="vGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#FFE07A" />
            <stop offset="50%" stopColor="#FFC93C" />
            <stop offset="100%" stopColor="#E5A100" />
          </linearGradient>
          <filter id="vGlow" x="-30%" y="-30%" width="160%" height="160%">
            <feGaussianBlur stdDeviation="6" result="blur" />
            <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
        </defs>
        <circle cx={cx} cy={cy} r={ringR} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="10" />
        <motion.circle
          cx={cx} cy={cy} r={ringR} fill="none" stroke="url(#vGrad)"
          strokeWidth="10" strokeLinecap="round"
          strokeDasharray={circumference}
          initial={{ strokeDashoffset: circumference }}
          animate={{ strokeDashoffset: dashOffset }}
          transition={{ duration: 2.2, ease: "easeOut" }}
          filter="url(#vGlow)"
          transform={`rotate(-90 ${cx} ${cy})`}
        />
      </svg>

      <motion.div
        initial={{ scale: 0.75, opacity: 0 }}
        animate={{ scale: opening ? [1, 1.05, 0.95, 1] : 1, opacity: 1 }}
        transition={{ duration: opening ? 2.2 : 1, ease: "easeOut" }}
        className="absolute rounded-full"
        style={{
          left: cx - 170, top: cy - 170, width: 340, height: 340,
          background: "radial-gradient(circle at 30% 25%, #6a5836 0%, #3a2e1a 45%, #1f180b 100%)",
          boxShadow: "inset 0 8px 20px rgba(255,255,255,0.12), inset 0 -8px 30px rgba(0,0,0,0.65), 0 30px 80px rgba(0,0,0,0.6)",
        }}
      >
        <div className="absolute inset-4 rounded-full" style={{ background: "radial-gradient(circle at 30% 25%, #7a6540 0%, #40311a 60%, #221806 100%)" }} />
        <div className="absolute inset-10 rounded-full" style={{ background: "radial-gradient(circle at 30% 25%, #8c7548 0%, #4b3a1f 55%, #251a09 100%)" }} />
        {/* Rivets */}
        {Array.from({ length: 16 }).map((_, i) => {
          const angle = (i / 16) * Math.PI * 2;
          const rivetR = 148;
          const rx = 170 + Math.cos(angle) * rivetR - 6;
          const ry = 170 + Math.sin(angle) * rivetR - 6;
          return (
            <span key={i} className="absolute rounded-full" style={{
              left: rx, top: ry, width: 12, height: 12,
              background: "radial-gradient(circle at 30% 30%, #ffe17a, #b8860b 55%, #4a340a 100%)",
              boxShadow: "inset 0 1px 2px rgba(255,255,255,0.6), 0 0 4px rgba(255,201,60,0.5)",
            }} />
          );
        })}
        {/* Spokes */}
        <motion.div
          className="absolute"
          style={{ left: 70, top: 70, width: 200, height: 200 }}
          animate={{ rotate: opening ? [0, 540] : (unlocked ? 0 : 360) }}
          transition={{ duration: opening ? 2.5 : 24, repeat: opening ? 0 : (unlocked ? 0 : Infinity), ease: opening ? "easeInOut" : "linear" }}
        >
          {[0, 60, 120, 180, 240, 300].map((deg, i) => (
            <div key={i} className="absolute left-1/2 top-1/2" style={{
              width: 96, height: 10, marginLeft: -48, marginTop: -5, transform: `rotate(${deg}deg)`,
              background: "linear-gradient(90deg, #4a340a 0%, #b8860b 25%, #ffe17a 50%, #b8860b 75%, #4a340a 100%)",
              borderRadius: 6, boxShadow: "inset 0 1px 1px rgba(255,255,255,0.35), 0 2px 3px rgba(0,0,0,0.5)",
            }} />
          ))}
          <div className="absolute left-1/2 top-1/2 rounded-full flex items-center justify-center" style={{
            width: 72, height: 72, marginLeft: -36, marginTop: -36,
            background: "radial-gradient(circle at 30% 30%, #fff1b3 0%, #E5A100 55%, #6a4e0a 100%)",
            boxShadow: "inset 0 2px 4px rgba(255,255,255,0.5), 0 6px 18px rgba(255,201,60,0.6)",
          }}>
            <motion.div animate={{ scale: opening ? [1, 1.5, 1] : [1, 1.08, 1] }} transition={{ duration: opening ? 1.5 : 2, repeat: opening ? 0 : Infinity }}>
              {unlocked ? <Unlock size={28} className="text-[#3a2200]" strokeWidth={2.5} /> : <Lock size={28} className="text-[#3a2200]" strokeWidth={2.5} />}
            </motion.div>
          </div>
        </motion.div>
      </motion.div>

      {/* Orbit sparkles */}
      {[0, 60, 120, 180, 240, 300].map((deg, i) => (
        <motion.div key={i} className="absolute top-1/2 left-1/2"
          animate={{ rotate: 360 }}
          transition={{ duration: 18 + i * 2, repeat: Infinity, ease: "linear" }}
          style={{ transformOrigin: "0 0" }} initial={{ rotate: deg }}
        >
          <motion.div
            className="absolute h-6 w-6 rounded-full"
            style={{ left: 210, top: -12, background: "radial-gradient(circle, #FFE07A 0%, #FFC93C 40%, transparent 70%)" }}
            animate={{ opacity: [0.4, 1, 0.4], scale: [0.8, 1.2, 0.8] }}
            transition={{ duration: 3 + i * 0.4, repeat: Infinity }}
          />
        </motion.div>
      ))}
    </div>
  );
}

function CeremonyOverlay() {
  return (
    <motion.div
      className="fixed inset-0 z-50 grid place-items-center bg-black/85 backdrop-blur-md"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      data-testid="vault-ceremony-overlay"
    >
      {/* Radial burst */}
      <motion.div
        className="absolute inset-0 pointer-events-none"
        initial={{ opacity: 0 }}
        animate={{ opacity: [0, 1, 0.4] }}
        transition={{ duration: 2.6, times: [0, 0.5, 1] }}
        style={{ background: "radial-gradient(circle at center, rgba(255,201,60,0.5), transparent 60%)" }}
      />
      {/* Central beacon */}
      <motion.div
        className="relative w-80 h-80 rounded-full"
        initial={{ scale: 0.4, opacity: 0 }}
        animate={{ scale: [0.4, 1.1, 1], opacity: 1, rotate: 360 }}
        transition={{ duration: 2.6, ease: "easeOut" }}
        style={{
          background: "radial-gradient(circle at 30% 30%, #fff1b3 0%, #FFC93C 40%, #E5A100 70%, #6a4e0a 100%)",
          boxShadow: "0 0 100px 30px rgba(255,201,60,0.7), inset 0 4px 20px rgba(255,255,255,0.5)",
        }}
      >
        <div className="absolute inset-0 grid place-items-center">
          <motion.div animate={{ scale: [1, 1.3, 1] }} transition={{ duration: 1.4, repeat: Infinity }}>
            <Unlock size={80} className="text-[#3a2200]" strokeWidth={2} />
          </motion.div>
        </div>
      </motion.div>
      {/* Light rays */}
      {Array.from({ length: 12 }).map((_, i) => (
        <motion.div
          key={i}
          className="absolute"
          style={{
            width: 4, height: "60vh",
            background: "linear-gradient(to bottom, rgba(255,201,60,0.7), transparent)",
            transformOrigin: "50% 0%",
            top: "50%", left: "50%",
            transform: `translate(-50%, 0) rotate(${i * 30}deg)`,
          }}
          initial={{ scaleY: 0, opacity: 0 }}
          animate={{ scaleY: 1, opacity: [0, 0.9, 0] }}
          transition={{ duration: 2.2, delay: 0.4 + i * 0.05 }}
        />
      ))}
      <div className="absolute bottom-16 left-0 right-0 text-center text-brand-cream">
        <motion.div className="font-display text-3xl" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}>
          Unlocking the Vault…
        </motion.div>
      </div>
    </motion.div>
  );
}

function RewardModal({ ceremony, campaign, onClose }) {
  const copy = () => {
    navigator.clipboard.writeText(ceremony.reward_code || "");
    toast.success("Reward code copied");
  };
  return (
    <motion.div
      className="fixed inset-0 z-50 grid place-items-center bg-black/80 backdrop-blur-md px-4"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      data-testid="vault-reward-modal"
    >
      <motion.div
        initial={{ scale: 0.7, opacity: 0, y: 30 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        transition={{ type: "spring", stiffness: 140, damping: 18 }}
        className="relative max-w-md w-full rounded-3xl p-8 text-brand-cream"
        style={{
          background: "linear-gradient(160deg, #1e0d3e 0%, #0e0a24 100%)",
          border: "1px solid rgba(255,201,60,0.35)",
          boxShadow: "0 30px 80px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.05)",
        }}
      >
        <button onClick={onClose} className="absolute top-4 right-4 text-brand-cream/60 hover:text-brand-cream" data-testid="vault-modal-close">
          <X size={20} />
        </button>

        <div className="text-center">
          <motion.div
            initial={{ scale: 0 }} animate={{ scale: 1, rotate: [0, 15, -15, 0] }}
            transition={{ delay: 0.1, duration: 0.8 }}
            className="mx-auto w-20 h-20 rounded-full grid place-items-center"
            style={{
              background: "radial-gradient(circle at 30% 30%, #fff1b3 0%, #FFC93C 50%, #E5A100 100%)",
              boxShadow: "0 0 24px rgba(255,201,60,0.6)",
            }}
          >
            <Gift size={36} className="text-[#3a2200]" strokeWidth={2.4} />
          </motion.div>
          <div className="mt-4 text-[11px] uppercase tracking-[0.32em] text-brand-yellow">You unlocked the Vault!</div>
          <div className="mt-2 font-display text-3xl">{campaign?.reward_title || "Community Reward"}</div>
          <div className="mt-2 text-sm text-brand-cream/70">{campaign?.reward_description}</div>
        </div>

        <div className="mt-6 rounded-2xl border border-brand-yellow/40 bg-brand-yellow/10 p-5">
          <div className="text-[10px] uppercase tracking-[0.28em] text-brand-yellow">Your redemption code</div>
          <div className="mt-1 flex items-center justify-between gap-3">
            <div className="font-mono text-2xl text-brand-yellow tracking-widest" data-testid="vault-reward-code">{ceremony.reward_code}</div>
            <button onClick={copy} className="rounded-full bg-white/10 hover:bg-white/20 border border-white/20 p-2 transition" data-testid="vault-copy-code">
              <Copy size={14} />
            </button>
          </div>
          <div className="mt-3 text-[11px] text-brand-cream/60">Show this code at any Pop Panda Go outlet to redeem your reward.</div>
        </div>

        <button
          onClick={onClose}
          className="mt-6 w-full rounded-full px-6 py-3 text-sm font-semibold text-[#1a0f2f]"
          style={{
            background: "linear-gradient(135deg, #FFE07A 0%, #FFC93C 50%, #E5A100 100%)",
            boxShadow: "0 10px 28px rgba(255,201,60,0.4)",
          }}
          data-testid="vault-modal-continue"
        >
          Continue
        </button>
      </motion.div>
    </motion.div>
  );
}

function MiniStat({ label, value, icon: Icon }) {
  return (
    <div className="rounded-xl bg-black/30 border border-white/10 p-2.5">
      <div className="text-[9px] uppercase tracking-widest text-brand-cream/50 inline-flex items-center gap-1"><Icon size={10} /> {label}</div>
      <div className="mt-0.5 font-display text-lg text-brand-cream">{value}</div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-brand-cream/70">{label}</span>
      <span className="font-mono text-brand-cream">{value}</span>
    </div>
  );
}
